// Step 2 — Map Surfaces: confirm / fix the suggested surface for every takeoff line.
import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCheck, Layers, X } from "lucide-react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";
import { useAlert } from "context/AlertContext";
import { Button, Card, CardHeader, Checkbox, ProgressBar, Segmented, TH } from "../../components/ui";
import MappingGroup from "./map-surfaces/MappingGroup";
import { FILTERS, groupBySection, mappingStats } from "./map-surfaces/mapping-utils";

export default function StepMapSurfaces({
  lines,
  config,
  mappings,
  setMappings,
  skipUnmapped,
  setSkipUnmapped,
  remember,
  setRemember,
  onBack,
  onContinue,
  continuing,
}) {
  const { showAlert } = useAlert();
  const [filter, setFilter] = useState("ALL");
  const [selected, setSelected] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [bulkSurface, setBulkSurface] = useState("");
  const [editingId, setEditingId] = useState(null);

  const surfaceById = useMemo(() => Object.fromEntries(config.surfaces.map((s) => [s.id, s])), [config]);
  const surfaceOptions = useMemo(() => config.surfaces.map((s) => ({ value: s.id, label: s.name })), [config]);

  const stats = mappingStats(lines, mappings);
  const allGroups = useMemo(() => groupBySection(lines), [lines]);
  const visibleLines = lines.filter((l) => FILTERS[filter](l, mappings));
  const groups = groupBySection(visibleLines);
  const pct = stats.total ? (stats.mapped / stats.total) * 100 : 0;
  const canContinue = stats.mapped > 0 && (stats.unmapped === 0 || skipUnmapped);

  const filterOptions = [
    { value: "ALL", label: "All", count: stats.total },
    { value: "UNMAPPED", label: "Unmapped", count: stats.unmapped },
    { value: "LOW", label: "Low confidence", count: stats.low },
    { value: "WARNINGS", label: "Warnings", count: stats.warnings },
  ];

  const pick = (lineId, surfaceId) => setMappings((m) => ({ ...m, [lineId]: { surfaceId, accepted: true } }));

  const toggleRow = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleGroup = (groupLines, select) =>
    setSelected((s) => {
      const next = new Set(s);
      groupLines.forEach((l) => (select ? next.add(l.id) : next.delete(l.id)));
      return next;
    });

  const toggleCollapse = (section) =>
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  const applyBulk = () => {
    if (!bulkSurface || selected.size === 0) return;
    setMappings((m) => {
      const next = { ...m };
      selected.forEach((id) => {
        next[id] = { surfaceId: bulkSurface, accepted: true };
      });
      return next;
    });
    showAlert({ type: "success", message: `Applied “${surfaceById[bulkSurface]?.name}” to ${selected.size} line${selected.size > 1 ? "s" : ""}` });
    setSelected(new Set());
    setBulkSurface("");
  };

  const acceptHigh = () => {
    const count = stats.pendingHigh;
    setMappings((m) => {
      const next = { ...m };
      lines.forEach((l) => {
        if (l.confidence === "High" && next[l.id]?.surfaceId) next[l.id] = { ...next[l.id], accepted: true };
      });
      return next;
    });
    showAlert({ type: "success", message: `${count} high-confidence suggestion${count === 1 ? "" : "s"} accepted` });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          icon={Layers}
          title="Map takeoff lines to surfaces"
          subtitle="Suggestions come from keywords in the raw line name. Click a surface to change it."
          actions={
            <Button variant="secondary" icon={CheckCheck} onClick={acceptHigh} disabled={stats.pendingHigh === 0}>
              Accept all High-confidence ({stats.pendingHigh})
            </Button>
          }
        />
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
          <Segmented options={filterOptions} value={filter} onChange={setFilter} ariaLabel="Filter lines" />
          {selected.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] font-semibold text-text_primary">{selected.size} selected</span>
              <div className="w-[260px]">
                <SearchableSelect
                  compact
                  options={surfaceOptions}
                  value={bulkSurface}
                  onChange={setBulkSurface}
                  placeholder="Surface for selected…"
                  ariaLabel="Surface to apply to selected lines"
                />
              </div>
              <Button variant="primary" onClick={applyBulk} disabled={!bulkSurface}>
                Apply surface to selected
              </Button>
              <Button variant="ghost" icon={X} onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </div>
          ) : (
            <span className="text-[11.5px] text-text_muted">Select rows to apply one surface to many lines.</span>
          )}
        </div>

        <div className="overflow-x-auto">
          {groups.length === 0 ? (
            <p className="p-8 text-center text-[12.5px] text-text_muted">No lines match this filter.</p>
          ) : (
            <table className="w-full min-w-[1180px] border-collapse">
              <thead>
                <tr>
                  <th className={`${TH} w-10`} aria-label="Select" />
                  <th className={TH}>Raw Name</th>
                  <th className={TH}>Location</th>
                  <th className={`${TH} text-right`}>Qty</th>
                  <th className={TH}>Unit</th>
                  <th className={`${TH} text-right`}>SF Qty</th>
                  <th className={TH}>Suggested Surface</th>
                  <th className={TH}>Confidence</th>
                  <th className={TH}>Status</th>
                </tr>
              </thead>
              {groups.map((g) => (
                <MappingGroup
                  key={g.section}
                  group={g}
                  allInGroup={allGroups.find((a) => a.section === g.section)?.lines || g.lines}
                  mappings={mappings}
                  surfaceById={surfaceById}
                  surfaceOptions={surfaceOptions}
                  skipUnmapped={skipUnmapped}
                  collapsed={collapsed.has(g.section)}
                  onToggleCollapse={() => toggleCollapse(g.section)}
                  selected={selected}
                  onToggleRow={toggleRow}
                  onToggleGroup={toggleGroup}
                  editingId={editingId}
                  setEditingId={setEditingId}
                  onPick={pick}
                />
              ))}
            </table>
          )}
        </div>
      </Card>

      {/* Sticky footer — right padding leaves room for the floating prototype badge */}
      <div className="sticky bottom-0 z-10 -mx-4 border-t border-border bg-surface px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <Button variant="secondary" icon={ArrowLeft} onClick={onBack}>
            Back
          </Button>
          <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
            <span className="text-[12.5px] text-text_primary">
              <span className="font-semibold tabular-nums">
                {stats.mapped} of {stats.total}
              </span>{" "}
              mapped
              {stats.unmapped > 0 && <span className="text-text_muted"> · {stats.unmapped} unmapped</span>}
            </span>
            <ProgressBar pct={pct} tone={stats.unmapped === 0 ? "success" : "primary"} />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Checkbox checked={remember} onChange={setRemember} label="Remember these mappings for future jobs" />
            {stats.unmapped > 0 && (
              <Checkbox checked={skipUnmapped} onChange={setSkipUnmapped} label={`Skip ${stats.unmapped} unmapped line${stats.unmapped > 1 ? "s" : ""}`} />
            )}
            <Button
              variant="primary"
              size="md"
              iconRight={ArrowRight}
              onClick={onContinue}
              disabled={!canContinue || continuing}
              title={canContinue ? undefined : "Map every line or choose to skip unmapped lines"}
            >
              {continuing ? "Saving…" : "Continue"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
